import { Component, OnInit } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

import { environment } from '../../../../environments/environment';

interface AssessmentAnswerOption {
  id: string;
  text: string;
  value: string;
  displayOrder: number;
  tooltip?: string;
}

interface AssessmentQuestionScore {
  id: string;
  score: number;
  category: string | null;
  assessmentAnswerOption: AssessmentAnswerOption;
}

interface QuestionDefinition {
  id: string;
  text: string;
  shortCode: string;
  inputType: number;
}

interface AssessmentTemplate {
  id: string;
  name: string;
  code: string;
  description: string;
  version: number;
  isActive: boolean;
}

interface AssessmentQuestion {
  id: string;
  displayOrder: number;
  isRequired: boolean;
  questionDefinition: QuestionDefinition;
  assessmentTemplate: AssessmentTemplate;
  assessmentQuestionScores: AssessmentQuestionScore[];
}

interface AssessmentQuestionItem {
  assessmentQuestion: AssessmentQuestion;
}

interface ApiResponse {
  data: {
    items: AssessmentQuestionItem[];
    index: number;
    size: number;
    count: number;
    pages: number;
    hasPrevious: boolean;
    hasNext: boolean;
  };
  status: number;
  fail: any;
  isSuccess: boolean;
}

interface CalculateAssessmentsScoresAnswer {
  assessmentQuestionId: string;
  assessmentAnswerOptionId: string;
}

interface CalculateAssessmentsScoresRequest {
  assessmentTemplateId: string;
  answers: CalculateAssessmentsScoresAnswer[];
}

interface CalculateAssessmentsScoresDto {
  assessmentTemplateId: string;
  assessmentTemplateCode: string;
  totalScore: number;
  riskCategoryName: string;
  description: string;
}

interface CalculateAssessmentsScoresResponse {
  data: {
    calculateAssessmentsScoresDtos: CalculateAssessmentsScoresDto[];
  };
  status: number;
  fail: any;
  isSuccess: boolean;
}

interface QuestionVM {
  id: string;
  text: string;
  shortCode: string;
  templateId: string;
  templateName: string;
  templateCode: string;
  isRequired: boolean;
  displayOrder: number;
  inputType: number;
  options: {
    id: string;
    text: string;
    value: string;
    score: number;
    displayOrder: number;
    tooltip?: string;
  }[];
  answerOptionId?: string;
  touched?: boolean;
  isDisabled?: boolean;
  disabledReason?: string;
}

interface QuestionDependencyRule {
  triggerQuestionId: string;
  triggerAnswerValue: string;
  targetQuestionId: string;
}

const QUESTION_DEPENDENCY_RULES: QuestionDependencyRule[] = [
  {
    triggerQuestionId: '2130cd8e-9474-46f6-9f93-ccfc293818f5',
    triggerAnswerValue: 'LT_5',
    targetQuestionId: 'llsls232345-d423-4bc5-86c9-a5bac81b709e',
  },
  {
    triggerQuestionId: '213ded8e-9474-46f6-9f93-ccfc293818f5',
    triggerAnswerValue: '0',
    targetQuestionId: 'b81d1fv0-d423-4bc5-86c9-a5bag81b759e',
  },
  {
    triggerQuestionId: 'fofo0202-9474-46f6-9f93-202002osos',
    triggerAnswerValue: '0',
    targetQuestionId: 'b8ee1450-d423-4bc5-86c9-a5bac81b709e',
  },
];

interface QuestionGroup {
  title: string;
  code: string;
  questions: QuestionVM[];
  isOpen: boolean;
}

@Component({
  standalone: true,
  selector: 'app-assessment-questions',
  templateUrl: './assessment-questions.component.html',
  styleUrls: ['./assessment-questions.component.scss'],
  imports: [CommonModule, FormsModule, TranslateModule],
})
export class AssessmentQuestionsComponent implements OnInit {
  questionGroups: QuestionGroup[] = [];
  questions: QuestionVM[] = [];
  isLoading = false;
  isSubmitting = false;
  loadError: string | null = null;
  submitError: string | null = null;

  scores: CalculateAssessmentsScoresDto[] = [];

  activeTab: number = 1;

  activeTooltip: { text: string; x: number; y: number } | null = null;
  private tooltipTimeout: any = null;
  private pendingTooltipPosition: { x: number; y: number } | null = null;

  private readonly API_URL = `${environment.apiLocations.patient}/assessment`;

  constructor(
    private http: HttpClient,
    private translate: TranslateService,
  ) {
    const userLang = localStorage.getItem('app-lang') || 'tr-TR';
    this.translate.use(userLang);
  }

  ngOnInit(): void {
    this.fetchQuestions(this.activeTab);
  }

  setActiveTab(type: number): void {
    if (this.activeTab === type) return;
    this.activeTab = type;
    this.scores = [];
    this.fetchQuestions(this.activeTab);
  }

  fetchQuestions(woundType: number): void {
    this.isLoading = true;
    this.loadError = null;
    this.questions = [];
    this.questionGroups = [];
    const userLang = localStorage.getItem('app-lang') || 'tr-TR';
    const url = `${this.API_URL}/getListAssessmentQuestion?PageIndex=0&PageSize=100&WoundType=${woundType}&lang=${userLang}`;

    const lang = localStorage.getItem('app-lang') || 'tr-TR';
    const headers = new HttpHeaders().set('Content-Language', lang);

    this.http.get<ApiResponse>(url, { headers }).subscribe({
      next: (res) => {
        if (!res.isSuccess || !res.data?.items) {
          this.loadError = 'Failed to fetch question list.';
          this.isLoading = false;
          return;
        }

        const items = res.data.items;

        this.questions = items
          .map((item) => {
            const q = item.assessmentQuestion;

            const options = (q.assessmentQuestionScores || [])
              .map((s) => ({
                id: s.assessmentAnswerOption.id,
                text: s.assessmentAnswerOption.text,
                value: s.assessmentAnswerOption.value,
                score: s.score,
                displayOrder: s.assessmentAnswerOption.displayOrder,
                tooltip: s.assessmentAnswerOption.tooltip,
              }))
              .sort((a, b) => a.displayOrder - b.displayOrder);

            return {
              id: q.id,
              text: q.questionDefinition.text,
              shortCode: q.questionDefinition.shortCode,
              templateId: q.assessmentTemplate?.id,
              templateName: q.assessmentTemplate?.name,
              templateCode: q.assessmentTemplate?.code,
              // isRequired: q.isRequired,
              displayOrder: q.displayOrder,
              inputType: q.questionDefinition.inputType,
              options,
              answerOptionId: undefined,
            } as QuestionVM;
          })
          .sort((a, b) => a.displayOrder - b.displayOrder);

        // Group questions
        const groups = new Map<string, QuestionGroup>();

        this.questions.forEach((q) => {
          const key = q.templateCode || 'Other';
          if (!groups.has(key)) {
            groups.set(key, {
              title: q.templateCode || 'Other', // Using templateCode as title as requested
              code: q.templateCode,
              questions: [],
              isOpen: false,
            });
          }
          groups.get(key)?.questions.push(q);
        });

        this.questionGroups = Array.from(groups.values());
        this.evaluateQuestionDependencies();

        this.isLoading = false;
      },
      error: (err) => {
        console.error(err);
        this.loadError = 'An error occurred while fetching the question list.';
        this.isLoading = false;
      },
    });
  }

  trackByQuestionId(index: number, item: QuestionVM): string {
    return item.id;
  }

  getDisplayGroupTitle(group: QuestionGroup): string {
    return this.formatWifiDisplayText(group.title);
  }

  getDisplayQuestionText(text: string | undefined): string {
    return this.formatWifiDisplayText(text);
  }

  getDisplayOptionText(text: string | undefined): string {
    return this.formatWifiDisplayText(text, true);
  }

  getDisplayAssessmentCode(text: string | undefined): string {
    return this.formatWifiDisplayText(text);
  }

  onSubmit(): void {
    this.submitError = null;
    this.scores = [];

    const invalidRequired = this.questions.filter(
      (q) => q.isRequired && !q.answerOptionId,
    );

    if (invalidRequired.length > 0) {
      invalidRequired.forEach((q) => (q.touched = true));
      this.submitError = 'Please answer all required questions.';
      return;
    }

    this.isSubmitting = true;

    const groups = new Map<string, CalculateAssessmentsScoresRequest>();

    this.questions
      .filter((q) => q.answerOptionId)
      .forEach((q) => {
        if (!q.templateId || !q.answerOptionId) {
          return;
        }

        let group = groups.get(q.templateId);
        if (!group) {
          group = {
            assessmentTemplateId: q.templateId,
            answers: [],
          };
          groups.set(q.templateId, group);
        }

        group.answers.push({
          assessmentQuestionId: q.id,
          assessmentAnswerOptionId: q.answerOptionId,
        });
      });

    const body = {
      calculateAssessmentsScores: Array.from(groups.values()),
    };

    if (!body.calculateAssessmentsScores.length) {
      this.isSubmitting = false;
      this.submitError = 'No answers found to submit.';
      return;
    }

    console.log('Skor hesaplama POST body:', body);

    const url = `${this.API_URL}/calculateAssessmentsScores`;

    const lang = localStorage.getItem('app-lang') || 'tr-TR';
    const headers = new HttpHeaders().set('Content-Language', lang);

    this.http
      .post<CalculateAssessmentsScoresResponse>(url, body, { headers })
      .subscribe({
        next: (res) => {
          this.isSubmitting = false;

          if (!res.isSuccess || !res.data?.calculateAssessmentsScoresDtos) {
            this.submitError = 'Failed to calculate score.';
            return;
          }

          this.scores = res.data.calculateAssessmentsScoresDtos;
        },
        error: (err) => {
          console.error(err);
          this.isSubmitting = false;
          this.submitError = 'An error occurred while calculating the score.';
        },
      });
  }

  resetAnswers(): void {
    this.questions.forEach((q) => {
      q.answerOptionId = undefined;
      q.touched = false;
    });
    this.evaluateQuestionDependencies();
    this.questionGroups.forEach((g) => (g.isOpen = false));
    this.scores = [];
    this.submitError = null;
  }

  onOptionClick(q: QuestionVM, optionId: string, event: Event): void {
    event.preventDefault();
    if (q.isDisabled) {
      return;
    }
    if (q.answerOptionId === optionId) {
      q.answerOptionId = undefined;
    } else {
      q.answerOptionId = optionId;
    }
    this.evaluateQuestionDependencies();
  }

  evaluateQuestionDependencies(): void {
    QUESTION_DEPENDENCY_RULES.forEach((rule) => {
      const targetQuestion = this.questions.find(
        (q) => q.id === rule.targetQuestionId,
      );
      if (targetQuestion) {
        targetQuestion.isDisabled = true;
        targetQuestion.disabledReason = this.translate.instant(
          'assessment.disabledReason',
        );
      }
    });

    QUESTION_DEPENDENCY_RULES.forEach((rule) => {
      const triggerQuestion = this.questions.find(
        (q) => q.id === rule.triggerQuestionId,
      );
      if (!triggerQuestion || !triggerQuestion.answerOptionId) {
        return;
      }

      const selectedOption = triggerQuestion.options.find(
        (opt) => opt.id === triggerQuestion.answerOptionId,
      );

      if (selectedOption && selectedOption.value !== rule.triggerAnswerValue) {
        const targetQuestion = this.questions.find(
          (q) => q.id === rule.targetQuestionId,
        );
        if (targetQuestion) {
          targetQuestion.isDisabled = false;
          targetQuestion.disabledReason = undefined;
        }
      } else {
        const targetQuestion = this.questions.find(
          (q) => q.id === rule.targetQuestionId,
        );
        if (targetQuestion) {
          targetQuestion.isDisabled = true;
          targetQuestion.answerOptionId = undefined;
        }
      }
    });
  }

  get hasAnyAnswer(): boolean {
    return this.questions.some((q) => q.answerOptionId);
  }

  showTooltip(event: MouseEvent, tooltip: string) {
    if (!tooltip) return;

    // Store the initial position
    this.pendingTooltipPosition = {
      x: event.clientX + 12,
      y: event.clientY - 12,
    };

    // Clear any existing timeout
    if (this.tooltipTimeout) {
      clearTimeout(this.tooltipTimeout);
    }

    // Set a 300ms delay before showing the tooltip
    this.tooltipTimeout = setTimeout(() => {
      if (this.pendingTooltipPosition) {
        this.activeTooltip = {
          text: tooltip,
          x: this.pendingTooltipPosition.x,
          y: this.pendingTooltipPosition.y,
        };
      }
    }, 300);
  }

  updateTooltipPosition(event: MouseEvent) {
    const newX = event.clientX + 12;
    const newY = event.clientY - 12;

    // Update pending position if tooltip hasn't appeared yet
    if (this.pendingTooltipPosition) {
      this.pendingTooltipPosition.x = newX;
      this.pendingTooltipPosition.y = newY;
    }

    // Update active tooltip position if it's already visible
    if (this.activeTooltip) {
      this.activeTooltip.x = newX;
      this.activeTooltip.y = newY;
    }
  }

  hideTooltip() {
    // Clear the timeout if user leaves before tooltip appears
    if (this.tooltipTimeout) {
      clearTimeout(this.tooltipTimeout);
      this.tooltipTimeout = null;
    }
    this.pendingTooltipPosition = null;
    this.activeTooltip = null;
  }

  private formatWifiDisplayText(
    text: string | undefined,
    removeIschemiaPrefix = false,
  ): string {
    if (!text) {
      return '';
    }

    let formatted = text
      .replace(/\bWIFI\b/g, 'WIfI')
      .replace(/\bWifi\b/g, 'WIfI')
      .replace(/^WIfI\s*\((Wound|Yara|Wond)\)\s*(Template|Skalası|Sjabloon)$/i, 'WIfI')
      .replace(/^WifiWound Template$/i, 'WIfI')
      .replace(/^WIfI Ischemia Classification\?$/i, 'WIfI Ischemia Classification');

    if (removeIschemiaPrefix) {
      formatted = formatted.replace(/^I[0-3]\s*-\s*/i, '');
      formatted = formatted.split('|')[0].trim();
    }

    return formatted;
  }
}
